import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import { apiHandler } from '../../../api/apihandler';
import {
  GROUP_LIST,
  GROUP_MEMBERS,
  GROUP_REMOVE_MEMBER,
  GROUP_DELETE,
  EXIT_GROUP,
} from '../../../api/constants';
import { user_data as userDataSelector } from '../../../store/setting/selectors.ts';
import EditGroup from '../../../components/chat/editGroup';
import AddNewMemberModal from '../../../components/chat/addNewMemberModal';
import EmptyState from '../../../components/nemonto/EmptyState';
import PeopleYouMayKnowWidget from '../../../components/nemonto/PeopleYouMayKnowWidget';
import MyStatsWidget from '../../../components/nemonto/MyStatsWidget';
import { NO_IMAGE, getCurrentUser, fullName } from './groupUtils';
import '../social/social-list-page.css';

const GroupDetailsPage = () => {
  const navigate = useNavigate();
  const { groupId } = useParams();
  const location = useLocation();
  const userDataRaw = useSelector(userDataSelector);
  const currentUser = getCurrentUser(userDataRaw);

  const [group, setGroup] = useState(location.state?.group || null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditGroup, setShowEditGroup] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);

  useEffect(() => {
    if (!group) {
      // Deep-link / refresh: no state was passed, so recover the group from the list.
      getGroupFromList();
    }
    getMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  async function getGroupFromList() {
    try {
      const res = await apiHandler(GROUP_LIST, 'POST', { page: 1, limit: 100 });
      if (res.data?.response?.status === 200) {
        const found = (res?.data?.data?.groups?.data || []).find(
          (g) => String(g.group_id) === String(groupId),
        );
        if (found) setGroup(found);
      }
    } catch (error) {
      console.error(error);
    }
  }

  async function getMembers() {
    setLoading(true);
    try {
      const res = await apiHandler(GROUP_MEMBERS, 'POST', { page: 1, limit: 100, group_id: groupId });
      if (res.data?.response?.status === 200) {
        setMembers(res?.data?.data?.group_members?.data || []);
      } else {
        setMembers([]);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  async function refreshAll() {
    await Promise.all([getGroupFromList(), getMembers()]);
  }

  const isOwner = group?.is_owner === 1;

  const confirmDeleteGroup = () => {
    Swal.fire({
      title: 'Delete group?',
      text: `"${group?.user_group?.name}" will be permanently deleted for everyone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
    }).then((result) => {
      if (result.isConfirmed) deleteGroup();
    });
  };

  async function deleteGroup() {
    try {
      const res = await apiHandler(GROUP_DELETE, 'DELETE', { group_id: groupId });
      if (res.data?.response?.status === 200) {
        navigate('/groups');
      } else {
        Swal.fire('Error', res?.response?.data?.data?.message || 'Unable to delete group.', 'error');
      }
    } catch (error) {
      console.error(error);
    }
  }

  const confirmLeaveGroup = () => {
    Swal.fire({
      title: 'Leave group?',
      text: `You will no longer see messages in "${group?.user_group?.name}".`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Leave',
      cancelButtonText: 'Cancel',
    }).then((result) => {
      if (result.isConfirmed) leaveGroup();
    });
  };

  async function leaveGroup() {
    try {
      const res = await apiHandler(EXIT_GROUP, 'DELETE', { group_id: groupId, member_id: currentUser?.id });
      if (res.data?.response?.status === 200) {
        navigate('/groups');
      } else {
        Swal.fire('Error', res?.response?.data?.data?.message || 'Unable to leave group.', 'error');
      }
    } catch (error) {
      console.error(error);
    }
  }

  const confirmRemoveMember = (member) => {
    Swal.fire({
      title: 'Remove member?',
      text: `Remove ${fullName(member?.user)} from this group?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Remove',
      cancelButtonText: 'Cancel',
    }).then((result) => {
      if (result.isConfirmed) removeMember(member);
    });
  };

  async function removeMember(member) {
    try {
      const res = await apiHandler(GROUP_REMOVE_MEMBER, 'DELETE', {
        group_id: groupId,
        member_id: member?.user?.id,
      });
      if (res.data?.response?.status === 200) {
        getMembers();
      } else {
        Swal.fire('Error', res?.response?.data?.data?.message || 'Unable to remove member.', 'error');
      }
    } catch (error) {
      console.error(error);
    }
  }

  return (
    <div className="nmsl-layout">
      <div className="nmsl-main">
        <div className="nmsl-page-header">
          <div className="nmsl-header">
            <button type="button" className="nmsl-icon-btn" onClick={() => navigate(-1)} title="Back">
              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>arrow_back</span>
            </button>
            <h3 className="nmsl-title">Group Details</h3>
          </div>
        </div>

        <div className="nmsl-main-body">
          <div className="nmgd-hero">
            <div className="nmgd-avatar-wrap">
              <img
                src={group?.user_group?.image || NO_IMAGE}
                alt={group?.user_group?.name}
                className="nmgd-avatar"
              />
              {isOwner && (
                <button
                  type="button"
                  className="nmgd-edit-badge"
                  onClick={() => setShowEditGroup(true)}
                  title="Edit group"
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 15 }}>edit</span>
                </button>
              )}
            </div>
            <h4 className="nmgd-name">{group?.user_group?.name}</h4>
            <p className="nmgd-meta">
              {members.length} {members.length === 1 ? 'member' : 'members'}
            </p>

            <div className="nmgd-actions">
              <button
                type="button"
                className="nmgd-btn-primary"
                onClick={() => navigate(`/chat/group/${groupId}`, { state: { header: { title: group?.user_group?.name, avatarUrl: group?.user_group?.image, isGroup: true } } })}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 17 }}>chat</span>
                Open Chat
              </button>
              {isOwner ? (
                <button type="button" className="nmgd-btn-danger" onClick={confirmDeleteGroup}>
                  Delete Group
                </button>
              ) : (
                <button type="button" className="nmgd-btn-danger" onClick={confirmLeaveGroup}>
                  Leave Group
                </button>
              )}
            </div>
          </div>

          <div className="nmgd-section-head">
            <h3>Members</h3>
            {isOwner && (
              <button type="button" className="nmgd-add-btn" onClick={() => setShowAddMemberModal(true)}>
                <span className="material-symbols-outlined" style={{ fontSize: 15 }}>person_add</span>
                Add Members
              </button>
            )}
          </div>

          {loading && <p className="nmsl-loading">Loading members…</p>}
          {!loading && members.length === 0 && (
            <EmptyState icon="group" title="No members yet" sub="Add members above to start cooking together." />
          )}

          {!loading && members.length > 0 && (
            <div className="nmsl-grid">
              {members.map((member) => (
                <div key={member.id} className="nmsl-card">
                  <img
                    src={member?.user?.user_setting?.photo || NO_IMAGE}
                    alt={fullName(member?.user)}
                    className="nmsl-avatar"
                  />
                  <div className="nmsl-body">
                    <div className="nmsl-name">
                      {fullName(member?.user)}
                      {member?.is_owner === 1 && <span className="nmsl-badge">Admin</span>}
                    </div>
                  </div>
                  {isOwner && member?.user?.id !== currentUser?.id && (
                    <button
                      type="button"
                      className="nmsl-icon-btn"
                      onClick={() => confirmRemoveMember(member)}
                      title="Remove member"
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: 16 }}>person_remove</span>
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <aside className="nmsl-rail">
        <PeopleYouMayKnowWidget />
        <MyStatsWidget />
      </aside>

      {isOwner && group && (
        <EditGroup
          showEditGroup={showEditGroup}
          setShowEditGroup={setShowEditGroup}
          groupDetails={group}
          getGroupListApi={refreshAll}
        />
      )}
      {isOwner && group && (
        <AddNewMemberModal
          showAddMemberModal={showAddMemberModal}
          setShowAddMemberModal={setShowAddMemberModal}
          groupDetails={group}
        />
      )}
    </div>
  );
};

export default GroupDetailsPage;
